/**
 * @fileoverview Overlay de Transmisión Cintillo OBS / vMix / CasparCG: 'Arma la Playlist' (Tile ID 46).
 * Ruta: /abcdef/overlays/playlist
 * 
 * Implementa arquitectura Zero-Canvas homologada a OverlayQuizz y PluckingRooster:
 *  - Estructura BEM .overlayquizz / .overlayquizz__banner / .overlayquizz__banner--v2.
 *  - Fondo 100% transparente para fuentes de navegador OBS / vMix.
 *  - Adaptabilidad de resolución para cintillos de 1536x180 px (--Image--Md) y 1920x180 px (--Image--Lg).
 *  - Offset dinámico vertical `offsetY` / `overlayOffsetY` con `style={{ bottom: `${offsetY}px` }}`.
 *  - Códigos QR con `QRCodeSVG` de `qrcode.react`, URL `${origin}/playlist?s=obs`, logotipo central integrado y soporte para 1 o 2 QRs según `settings.qrCount`.
 *  - Cintillo animado "Sonando en el Estadio" (Now Playing) con disco de vinil 3D giratorio, ecualizador GPU a 60 FPS,
 *    créditos de aficionados y métricas de votos en vivo.
 *  - Sincronización en tiempo real vía WebSocket nativo ADSL canal `overlay:playlist`.
 * 
 * @module components/Overlays/Playlist
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { GetNowPlaying, GetSettings, GetActivePlaylist } from '../../utilities/BL/Admin/Overlays/Playlist';
import { ConnectWebSocketForPlaylistAdmin, DisconnectWebSocketForPlaylistAdmin } from '../../utilities/WS/Admin/PlaylistWS';

/**
 * Resuelve rutas relativas o absolutas a URLs completas hacia la API o assets locales.
 * @param {string|null} mediaPath
 * @returns {string}
 */
const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const rawApiUrl = process.env.REACT_APP_API_URL || '';
    const apiBaseUrl = rawApiUrl.replace(/\/api\/?$/, '');
    const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
    return apiBaseUrl ? `${apiBaseUrl}${sanitizedMediaPath}` : sanitizedMediaPath;
};

/**
 * Configuración por defecto para el Overlay de Playlist.
 */
const DEFAULT_SETTINGS = {
    screenMode: 0,
    isOverlayVisible: true,
    isOverlayNowPlayingVisible: false,
    screenTitle: 'ARMA LA PLAYLIST',
    screenSubtitle: '¿QUÉ CANCIÓN O ARTISTA FALTA EN LA PLAYLIST?',
    qrUrl: '',
    qrCenterLogoUrl: '',
    qrCount: 1, // 1: Single (1 QR), 2: Double (2 QRs)
    isScreenQrVisible: true,
    showFanNameOnScreen: true,
    showVoteCountOnScreen: true,
    offsetY: 0,
    overlayOffsetY: 0,
    overlayBackground1536Url: '',
    overlayBackground1920Url: ''
};

/**
 * Componente maestro del Overlay Cintillo de Playlist para OBS / vMix.
 */
export const Playlist = () => {
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [currentSong, setCurrentSong] = useState(null);
    const [, setActivePlaylist] = useState([]);
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [flowState, setFlowState] = useState('idle'); // 'idle' | 'entering' | 'visible' | 'fadingOut'

    const flowTimerRef = useRef(null);

    // =========================================================================
    // 1. FORZAR FONDO 100% TRANSPARENTE PARA OBS / VMIX
    // =========================================================================
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const rootElem = document.getElementById('root');

        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        if (rootElem) rootElem.style.backgroundColor = 'transparent';

        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
            if (rootElem) rootElem.style.removeProperty('background-color');
        };
    }, []);

    // =========================================================================
    // 2. GESTIÓN DE CICLO DE VIDA Y ANIMACIONES DE TRANSICIÓN (FLOW STATE)
    // =========================================================================
    useEffect(() => {
        let timeout;
        if (flowState === 'entering') {
            timeout = setTimeout(() => setFlowState('visible'), 500);
        } else if (flowState === 'fadingOut') {
            timeout = setTimeout(() => setFlowState('idle'), 500);
        }
        return () => clearTimeout(timeout);
    }, [flowState]);

    const handleOverlayVisibilityChange = useCallback((visible) => {
        if (visible) {
            setFlowState((prev) => {
                if (prev === 'idle' || prev === 'fadingOut') {
                    return 'entering';
                }
                return prev;
            });
        } else {
            setFlowState((prev) => {
                if (prev !== 'idle' && prev !== 'fadingOut') {
                    return 'fadingOut';
                }
                return prev;
            });
        }
    }, []);

    // =========================================================================
    // 3. CARGA DE DATOS INICIALES DESDE LA API REST
    // =========================================================================
    const loadInitialData = useCallback(async () => {
        try {
            const [nowPlayingRes, settingsRes, activeRes] = await Promise.all([
                GetNowPlaying(),
                GetSettings(),
                GetActivePlaylist()
            ]);

            if (settingsRes && settingsRes.success && settingsRes.data) {
                const sData = settingsRes.data;
                const calculatedOffsetY = sData.offsetY !== undefined ? Number(sData.offsetY) : (sData.overlayOffsetY !== undefined ? Number(sData.overlayOffsetY) : 0);

                setSettings(prev => ({
                    ...prev,
                    ...sData,
                    offsetY: calculatedOffsetY,
                    overlayOffsetY: calculatedOffsetY,
                    isOverlayVisible: sData.isOverlayVisible !== undefined ? Boolean(sData.isOverlayVisible) : prev.isOverlayVisible,
                    isOverlayNowPlayingVisible: sData.isOverlayNowPlayingVisible !== undefined ? Boolean(sData.isOverlayNowPlayingVisible) : prev.isOverlayNowPlayingVisible
                }));

                if (sData.isOverlayVisible) {
                    setFlowState('visible');
                } else {
                    setFlowState('idle');
                }
            }

            if (nowPlayingRes && nowPlayingRes.success && nowPlayingRes.data) {
                if (nowPlayingRes.data.currentSong) {
                    setCurrentSong(nowPlayingRes.data.currentSong);
                }
                if (nowPlayingRes.data.screenMode !== undefined) {
                    setSettings(prev => ({ ...prev, screenMode: Number(nowPlayingRes.data.screenMode) }));
                }
            }

            if (activeRes && activeRes.success && Array.isArray(activeRes.data)) {
                setActivePlaylist(activeRes.data);
            }
        } catch (err) {
            console.error('[PlaylistOverlay] Error cargando estado inicial:', err);
        }
    }, []);

    // =========================================================================
    // 4. MANEJADOR REACTIVO DE EVENTOS WEBSOCKET (CANAL 'overlay:playlist')
    // =========================================================================
    const handleWebSocketMessage = useCallback((packet) => {
        if (!packet || typeof packet !== 'object') return;

        const action = packet.action || packet.type;
        const data = packet.data || packet.payload || packet;

        switch (action) {
            case 'now_playing_changed':
            case 'playlist.now_playing':
                if (data) {
                    if (data.currentSong !== undefined) {
                        setCurrentSong(data.currentSong || null);
                    } else if (data.id && data.title) {
                        setCurrentSong(data);
                    }
                    if (data.screenMode !== undefined) {
                        setSettings(prev => ({ ...prev, screenMode: Number(data.screenMode) }));
                    }
                    if (data.isOverlayNowPlayingVisible !== undefined) {
                        setSettings(prev => ({ ...prev, isOverlayNowPlayingVisible: Boolean(data.isOverlayNowPlayingVisible) }));
                    }
                }
                break;

            case 'settings_updated':
            case 'playlist.settings':
            case 'playlist.state':
            case 'state':
            case 'playlist_state':
                if (data) {
                    setSettings(prev => ({
                        ...prev,
                        ...data,
                        offsetY: data.offsetY !== undefined ? Number(data.offsetY) : (data.overlayOffsetY !== undefined ? Number(data.overlayOffsetY) : prev.offsetY),
                        overlayOffsetY: data.overlayOffsetY !== undefined ? Number(data.overlayOffsetY) : (data.offsetY !== undefined ? Number(data.offsetY) : prev.overlayOffsetY),
                        isOverlayVisible: data.isOverlayVisible !== undefined ? Boolean(data.isOverlayVisible) : prev.isOverlayVisible,
                        isOverlayNowPlayingVisible: data.isOverlayNowPlayingVisible !== undefined ? Boolean(data.isOverlayNowPlayingVisible) : prev.isOverlayNowPlayingVisible
                    }));
                    if (data.currentPlayingSong !== undefined) {
                        setCurrentSong(data.currentPlayingSong);
                    }
                    if (data.isOverlayVisible !== undefined) {
                        handleOverlayVisibilityChange(Boolean(data.isOverlayVisible));
                    }
                }
                break;

            case 'playlist_updated':
            case 'playlist.queue':
                if (Array.isArray(data)) {
                    setActivePlaylist(data);
                }
                break;

            case 'dynamic_reset':
            case 'playlist.reset':
                setCurrentSong(null);
                setActivePlaylist([]);
                break;

            case 'overlay_visibility_changed':
            case 'config':
            case 'playlist_config':
                if (data) {
                    if (data.isOverlayVisible !== undefined) {
                        setSettings(prev => ({ ...prev, isOverlayVisible: Boolean(data.isOverlayVisible) }));
                        handleOverlayVisibilityChange(Boolean(data.isOverlayVisible));
                    }
                    if (data.offsetY !== undefined || data.overlayOffsetY !== undefined) {
                        const newOffset = Number(data.offsetY !== undefined ? data.offsetY : data.overlayOffsetY);
                        setSettings(prev => ({ ...prev, offsetY: newOffset, overlayOffsetY: newOffset }));
                    }
                }
                break;

            default:
                break;
        }
    }, [handleOverlayVisibilityChange]);

    // Conectar WebSocket nativo ADSL
    useEffect(() => {
        loadInitialData();

        ConnectWebSocketForPlaylistAdmin(
            handleWebSocketMessage,
            (status) => setConnectionStatus(status),
            'overlay:playlist'
        );

        return () => {
            DisconnectWebSocketForPlaylistAdmin();
            if (flowTimerRef.current) clearTimeout(flowTimerRef.current);
        };
    }, [loadInitialData, handleWebSocketMessage]);

    // =========================================================================
    // 5. CÁLCULO DE PROPIEDADES VISUALES Y RECURSOS
    // =========================================================================
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const qrTargetUrl = `${origin}/playlist?s=obs`;

    const qrCenterLogo = settings.qrCenterLogoUrl
        ? resolveMediaUrl(settings.qrCenterLogoUrl)
        : '/img/overlays/rubaquiz/interaktia-transparent.png';

    const isDualQr = Number(settings.qrCount) === 2 || String(settings.qrCount).toLowerCase() === 'double' || settings.qrCount === '2';
    const isNowPlayingMode = Boolean(settings.isOverlayNowPlayingVisible) && Boolean(currentSong);

    const defaultBg1536 = '/img/overlays/rooster/santos-femenil/cintillo-1536-x-180-px.png';
    const defaultBg1920 = '/img/overlays/rooster/santos-femenil/cintillo-1920-x-180-px.png';

    const bg1536 = resolveMediaUrl(settings.overlayBackground1536Url || settings.overlayBackground1920Url) || defaultBg1536;
    const bg1920 = resolveMediaUrl(settings.overlayBackground1920Url || settings.overlayBackground1536Url) || defaultBg1920;
    const offsetY = Number(settings.offsetY !== undefined ? settings.offsetY : (settings.overlayOffsetY || 0));

    // ZERO-CANVAS: 0% CPU/GPU cuando el overlay está inactivo
    if (flowState === 'idle') {
        return null;
    }

    return (
        <div
            className={`overlayquizz overlayquizz--playlist ${isDualQr ? 'overlayquizz--playlist-dual' : 'overlayquizz--playlist-single overlayquizz--single'}`}
            style={{ bottom: `${offsetY}px` }}
        >
            <div className={`overlayquizz__banner overlayquizz__banner--${flowState}`}>
                {/* ========================================================================= */}
                {/* CASO 1: MODO NOW PLAYING ("SONANDO EN EL ESTADIO")                         */}
                {/* ========================================================================= */}
                {isNowPlayingMode ? (
                    <div className="overlayquizz__banner__nowplaying PlaylistOverlay__NowPlaying">
                        {/* Lado izquierdo: Disco de vinil 3D y detalles de la pista */}
                        <div className="PlaylistOverlay__NowPlayingLeft">
                            <div className="PlaylistOverlay__Vinyl">
                                <div className="PlaylistOverlay__VinylCenter">
                                    <div className="PlaylistOverlay__VinylHole"></div>
                                </div>
                            </div>

                            <div className="PlaylistOverlay__SongInfo">
                                <div className="PlaylistOverlay__Badges">
                                    <span className="PlaylistOverlay__LiveBadge">
                                        <span className="PlaylistOverlay__LiveDot"></span> Sonando en el Estadio
                                    </span>
                                    {settings.showVoteCountOnScreen !== false && currentSong.voteCount > 0 && (
                                        <span className="PlaylistOverlay__VoteBadge">
                                            🔥 {currentSong.voteCount} votos
                                        </span>
                                    )}
                                </div>

                                <h2 className="PlaylistOverlay__SongTitle" title={currentSong.title}>
                                    {currentSong.title}
                                </h2>

                                {currentSong.artistName && (
                                    <h3 className="PlaylistOverlay__ArtistName" title={currentSong.artistName}>
                                        {currentSong.artistName}
                                    </h3>
                                )}

                                {settings.showFanNameOnScreen !== false && currentSong.fanName && (
                                    <div className="PlaylistOverlay__FanCredit">
                                        <i className="bi bi-heart-fill text-danger me-1"></i>
                                        Sugerida por: <strong>{currentSong.fanName}</strong>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Lado derecho: Ecualizador de Audio 60 FPS + QR */}
                        <div className="PlaylistOverlay__NowPlayingRight">
                            <div className="PlaylistOverlay__Equalizer">
                                <span className="PlaylistOverlay__EqualizerBar"></span>
                                <span className="PlaylistOverlay__EqualizerBar"></span>
                                <span className="PlaylistOverlay__EqualizerBar"></span>
                                <span className="PlaylistOverlay__EqualizerBar"></span>
                                <span className="PlaylistOverlay__EqualizerBar"></span>
                                <span className="PlaylistOverlay__EqualizerBar"></span>
                                <span className="PlaylistOverlay__EqualizerBar"></span>
                            </div>

                            {settings.isScreenQrVisible !== false && (
                                <div className="PlaylistOverlay__QRCard">
                                    <QRCodeSVG
                                        value={qrTargetUrl}
                                        size={118}
                                        level="H"
                                        bgColor="#FFFFFF"
                                        fgColor="#0B1528"
                                        imageSettings={{
                                            src: qrCenterLogo,
                                            height: 24,
                                            width: 24,
                                            excavate: true
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="overlayquizz__banner--v2">
                        {/* ========================================================================= */}
                        {/* CASO 2: MODO CONVOCATORIA / INVITACIÓN (1536px / 1920px) CON 1 O 2 QRs     */}
                        {/* ========================================================================= */}
                        {/* Imágenes responsivas 1536px y 1920px */}
                        <img
                            src={bg1536}
                            className="overlayquizz__banner--v2__Image--Md"
                            alt="Banner Arma la Playlist 1536"
                        />
                        <img
                            src={bg1920}
                            className="overlayquizz__banner--v2__Image--Lg"
                            alt="Banner Arma la Playlist 1920"
                        />

                        {/* Código QR Izquierdo */}
                        {settings.isScreenQrVisible !== false && (
                            <div className="overlayquizz__banner--v2__QR-Overlay overlayquizz__banner--v2__QR-Overlay--left">
                                <QRCodeSVG
                                    value={qrTargetUrl}
                                    size={256}
                                    style={{ width: '92%', height: '92%', display: 'block' }}
                                    bgColor="#d9d9d9"
                                    fgColor="#000000"
                                    level="H"
                                    imageSettings={{
                                        src: qrCenterLogo,
                                        x: undefined,
                                        y: undefined,
                                        height: 52,
                                        width: 52,
                                        excavate: true
                                    }}
                                />
                            </div>
                        )}

                        {/* Código QR Derecho (Solo en modo 2 QRs) */}
                        {isDualQr && settings.isScreenQrVisible !== false && (
                            <div className="overlayquizz__banner--v2__QR-Overlay overlayquizz__banner--v2__QR-Overlay--right">
                                <QRCodeSVG
                                    value={qrTargetUrl}
                                    size={256}
                                    style={{ width: '92%', height: '92%', display: 'block' }}
                                    bgColor="#d9d9d9"
                                    fgColor="#000000"
                                    level="H"
                                    imageSettings={{
                                        src: qrCenterLogo,
                                        x: undefined,
                                        y: undefined,
                                        height: 52,
                                        width: 52,
                                        excavate: true
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {connectionStatus === 'error' && (
                <div style={{ position: 'fixed', bottom: 5, right: 5, background: 'red', color: 'white', padding: '3px 8px', fontSize: '9px', borderRadius: '4px', opacity: 0.8, zIndex: 99999 }}>
                    D
                </div>
            )}
        </div>
    );
};

export default Playlist;
